import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AutoplayOverlay } from '@/store/videoFeatures';
import { cn } from '@/lib/utils';

interface AutoplayTextFieldsWithCounterProps {
  autoplayOverlay: AutoplayOverlay;
  updateAutoplayOverlay: (key: keyof AutoplayOverlay, value: any) => void;
  hideTitle?: boolean;
}

const MAX_CHARS_TOP = 40;
const MAX_CHARS_BOTTOM = 90;

// Define quais templates usam quais campos
const TEMPLATE_FIELD_USAGE = {
  'small': { usesTextTop: true, usesTextBottom: false, defaultTop: 'Clique para ouvir', defaultBottom: '' },
  'autoPlay': { usesTextTop: true, usesTextBottom: true, defaultTop: 'Ative o som', defaultBottom: 'Seu video já começou' },
  'large': { usesTextTop: false, usesTextBottom: true, defaultTop: '', defaultBottom: 'Clique para ouvir' },
  'fullScreen': { usesTextTop: true, usesTextBottom: true, defaultTop: 'Seu vídeo já começou', defaultBottom: 'Ative o som' },
  'transparent': { usesTextTop: false, usesTextBottom: true, defaultTop: '', defaultBottom: 'Seu vídeo já começou' },
  'blur': { usesTextTop: true, usesTextBottom: true, defaultTop: 'Conteúdo Sensível', defaultBottom: 'Este vídeo apresenta conteúdo que os grandes produtores não querem que você assista!' },
  'animation': { usesTextTop: true, usesTextBottom: false, defaultTop: 'Seu video já começou', defaultBottom: '' }
};

export const AutoplayTextFieldsWithCounter: React.FC<AutoplayTextFieldsWithCounterProps> = ({
  autoplayOverlay,
  updateAutoplayOverlay,
  hideTitle = false
}) => {
  useLanguage();
  const templateId = autoplayOverlay.templateId || 'autoPlay';
  const fieldUsage = TEMPLATE_FIELD_USAGE[templateId as keyof typeof TEMPLATE_FIELD_USAGE] || TEMPLATE_FIELD_USAGE['autoPlay'];
  const prevTemplateIdRef = useRef(templateId);

  const textTop = autoplayOverlay.textTop || '';
  const textBottom = autoplayOverlay.textBottom || '';

  // Limpar campos não usados quando o template mudar
  useEffect(() => {
    // Só executar se o templateId realmente mudou
    if (prevTemplateIdRef.current !== templateId) {
      prevTemplateIdRef.current = templateId;

      // NÃO preencher automaticamente - apenas limpar campos não usados
      if (!fieldUsage.usesTextTop && textTop) {
        updateAutoplayOverlay('textTop', '');
      }

      if (!fieldUsage.usesTextBottom && textBottom) {
        updateAutoplayOverlay('textBottom', '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  const getCharCountColor = (current: number, max: number) => {
    const percentage = current / max;
    if (percentage >= 1) return 'text-red-500';
    if (percentage >= 0.8) return 'text-amber-500';
    return 'text-[#94A3B8]';
  };

  return (
    <div className="space-y-3">
      {!hideTitle && (
        <h3 className="text-[14px] font-semibold text-foreground">
          {translateInterfaceText("Mensagens do Autoplay")}</h3>
      )}

      <div className="space-y-3">
        {/* Mensagem Principal */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label
              htmlFor="autoplayTextTop"
              className={cn(
                "text-[12px] font-medium",
                !fieldUsage.usesTextTop ? "text-muted-foreground/50" : "text-muted-foreground"
              )}
            >
              {translateInterfaceText("Mensagem Principal")}{!fieldUsage.usesTextTop && <span className="ml-1 text-[10px]">{translateInterfaceText("(não usado neste template)")}</span>}
            </Label>
            {fieldUsage.usesTextTop && (
              <span className={cn(
                "text-[10px] font-medium tabular-nums",
                getCharCountColor(textTop.length, MAX_CHARS_TOP)
              )}>
                {textTop.length}/{MAX_CHARS_TOP}
              </span>
            )}
          </div>
          <Input
            id="autoplayTextTop"
            value={textTop}
            onChange={(e) => {
              if (e.target.value.length <= MAX_CHARS_TOP) {
                updateAutoplayOverlay('textTop', e.target.value);
              }
            }}
            placeholder={fieldUsage.usesTextTop ? fieldUsage.defaultTop || translateInterfaceText("Ex.: Seu vídeo já começou") : translateInterfaceText("Campo não usado neste template")}
            disabled={!fieldUsage.usesTextTop}
            className={cn(
              "h-10 border-border text-[13px]",
              fieldUsage.usesTextTop
                ? "bg-muted/20 focus:border-primary focus:ring-primary/20 cursor-text"
                : "bg-muted/40 cursor-not-allowed text-muted-foreground/50 border-muted"
            )}
          />
        </div>

        {/* Mensagem Secundária */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label
              htmlFor="autoplayTextBottom"
              className={cn(
                "text-[12px] font-medium",
                !fieldUsage.usesTextBottom ? "text-muted-foreground/50" : "text-muted-foreground"
              )}
            >
              {translateInterfaceText("Mensagem Secundária")}{!fieldUsage.usesTextBottom && <span className="ml-1 text-[10px]">{translateInterfaceText("(não usado neste template)")}</span>}
            </Label>
            {fieldUsage.usesTextBottom && (
              <span className={cn(
                "text-[10px] font-medium tabular-nums",
                getCharCountColor(textBottom.length, MAX_CHARS_BOTTOM)
              )}>
                {textBottom.length}/{MAX_CHARS_BOTTOM}
              </span>
            )}
          </div>
          <Input
            id="autoplayTextBottom"
            value={textBottom}
            onChange={(e) => {
              if (e.target.value.length <= MAX_CHARS_BOTTOM) {
                updateAutoplayOverlay('textBottom', e.target.value);
              }
            }}
            placeholder={fieldUsage.usesTextBottom ? fieldUsage.defaultBottom || translateInterfaceText("Ex.: Ative o som para melhor experiência") : translateInterfaceText("Campo não usado neste template")}
            disabled={!fieldUsage.usesTextBottom}
            className={cn(
              "h-10 border-border text-[13px]",
              fieldUsage.usesTextBottom
                ? "bg-muted/20 focus:border-primary focus:ring-primary/20 cursor-text"
                : "bg-muted/40 cursor-not-allowed text-muted-foreground/50 border-muted"
            )}
          />
        </div>
      </div>
    </div>
  );
};
